import dayjs from 'dayjs';
import sortBy from 'lodash/sortBy';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';

import { sendMail } from '../../api/internal/mailer';
import { EMAIL_SUBJECTS_MAP } from '../../constants/contactForm';
import LoaderStore from '../../store/loader';
import { PrimaryButton } from '../Buttons/Buttons';
import GenericModalV2 from '../GenericModalV2';

import styles, { modalBorder, inputTextColor, white } from './EmailForm.module.scss';

const emailMap = EMAIL_SUBJECTS_MAP[process.env.appName];
const subjectOptions = Object.entries(emailMap).map(([key, { label }]) => ({ value: key, label }));

export default function EmailForm({ slug, positions = [], areas = [] }) {
  const router = useRouter();
  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    birthday: '',
    gender: '',
    telephone: '',
    mobilePhone: '',
    email: '',
    address: '',
    addressNumber: '',
    postal: '',
    city: '',
    area: '',
    position: '',
    message: '',
  });
  const [availableAreas, setAvailableAreas] = useState([]);
  const [availablePositions, setAvailablePositions] = useState([]);
  const [inputText, setInputText] = useState('Επίλεξε Αρχείο');
  const [selectedPosition, setSelectedPosition] = useState();
  const [selectedArea, setSelectedArea] = useState();
  const [selectedGender, setSelectedGender] = useState();
  const [startDate, setStartDate] = useState();

  const [modal, setModal] = useState({});

  const initialSubject = subjectOptions.find(v => v.value === slug) || undefined;
  const [selectedSubject, setSelectedSubject] = useState(initialSubject);

  useEffect(() => {
    const positionsSorted = sortBy(positions, position => position?.fields?.index);
    const positionsFormated = positionsSorted.map(position => ({
      value: position?.fields?.position,
      label: position?.fields?.position,
    }));
    const areasFormatted = areas.map(area => ({ value: area?.fields?.area, label: area?.fields?.area }));
    const areasSorted = sortBy(areasFormatted, 'value');
    setAvailablePositions(positionsFormated);
    setAvailableAreas(areasSorted);
  }, []);

  const style = {
    control: base => ({
      ...base,
      border: modalBorder,
      borderRadius: '0.25rem',
      '&:hover': {
        border: modalBorder,
      },
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
    }),
    option: styling => ({
      ...styling,
      color: inputTextColor,
    }),
  };

  function setTheme(theme) {
    return {
      ...theme,
      borderRadius: 0,
      colors: {
        ...theme.colors,
        primary: white,
        primary25: white,
      },
    };
  }

  const genders = [
    { value: 'Άνδρας', label: 'Άνδρας' },
    { value: 'Γυναίκα', label: 'Γυναίκα' },
  ];

  function onChangeSelectedArea(areaSelected) {
    setSelectedArea(areaSelected);
    setValues({ ...values, area: areaSelected.value });
  }
  function onChangeSelectedPosition(positionSelected) {
    setSelectedPosition(positionSelected);
    setValues({ ...values, position: positionSelected.value });
  }

  function onChangeSelectedGender(genderSelected) {
    setSelectedGender(genderSelected);
    setValues({ ...values, gender: genderSelected.value });
  }

  function onChangeDate(date) {
    setStartDate(date);
    const formattedDate = dayjs(date).format('DD/MM/YYYY');
    setValues({ ...values, birthday: formattedDate });
  }

  function onChangeInput(e) {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  }

  function onUploadFile(e) {
    const file = e.target.files[0];
    setInputText(file?.name || '');
  }

  async function onSubmit(e) {
    LoaderStore.addEvent('SEND_EMAIL');
    e.preventDefault();

    const emailCase = selectedSubject ? selectedSubject.value : 'default';
    const formParams = new FormData();
    formParams.append('emailCase', emailCase);
    Object.entries(values).forEach(([key, value]) => {
      if (value) formParams.append(key, value);
    });

    const file = e.target.cv ? e.target.cv.files[0] : 0;
    if (file) {
      formParams.append('attachment', file, file.name);
    }

    const { error } = await sendMail(formParams);
    if (error) {
      LoaderStore.removeEvent('SEND_EMAIL');
      setModal({
        isOpen: true,
        mainText:
          error.userMessage || 'Δεν ήταν δυνατή η αποστολή του μηνύματός σας. Παρακαλούμε προσπαθήστε ξανά σε λίγο.',
        onClickContinue() {
          setModal({
            ...modal,
            isOpen: false,
          });
        },
      });
      return;
    }

    LoaderStore.removeEvent('SEND_EMAIL');
    setModal({
      isOpen: true,
      submitText: slug !== 'career' ? 'Συνέχεια' : 'OK',
      mainText:
        slug !== 'career'
          ? `Το μήνυμά σας έχει σταλεί επιτυχώς στο αρμόδιο τμήμα της ${process.env.displayName}.`
          : `Σας ευχαριστούμε για την επικοινωνία. Το αίτημά σας έχει υποβληθεί επιτυχώς.
          
          Διεύθυνση Ανθρώπινου Δυναμικού ΑΝΕΔΗΚ ΚΡΗΤΙΚΟΣ`,
      onClickContinue() {
        setModal({
          ...modal,
          isOpen: false,
        });
        router.push('/');
      },
    });
  }

  return (
    <>
      <form action="/api/mailer" method="post" className={styles.emailForm} onSubmit={onSubmit}>
        <div className={styles.header}>
          <h3 className={styles.title}>{slug === 'career' ? 'Υποβολή Βιογραφικού' : 'Στείλε μας Email'}</h3>
          {slug !== 'career' && (
            <>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="emailSubject" className={styles.dropDown}>
                <Select
                  name="emailSubject"
                  inputId="emailSubject"
                  options={subjectOptions}
                  styles={style}
                  className={styles.selectInput}
                  value={selectedSubject}
                  onChange={setSelectedSubject}
                  placeholder="Θέμα"
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  theme={setTheme}
                />
              </label>
            </>
          )}
        </div>
        <div className={styles.container}>
          <label htmlFor="lastName" className={styles.labelSmaller}>
            <p className={styles.text}>
              Επώνυμο<span className={styles.span}>*</span>
            </p>
            <input
              id="lastName"
              name="lastName"
              type="text"
              required
              className={styles.input}
              value={values.names}
              onChange={onChangeInput}
            />
          </label>
          <label htmlFor="firstName" className={styles.labelSmaller}>
            <p className={styles.text}>
              Όνομα<span className={styles.span}>*</span>
            </p>
            <input
              id="firstName"
              name="firstName"
              type="text"
              required
              className={styles.input}
              value={values.names}
              onChange={onChangeInput}
            />
          </label>
        </div>
        {slug === 'career' && (
          <>
            <div className={styles.container}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="gender" className={styles.labelSmaller}>
                <p className={styles.text}>
                  Φύλο<span className={styles.span}>*</span>
                </p>
                <Select
                  name="gender"
                  inputId="gender"
                  options={genders}
                  styles={style}
                  className={styles.selectInput}
                  value={selectedGender}
                  onChange={onChangeSelectedGender}
                  placeholder="Επίλεξε το φύλο σου"
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  theme={setTheme}
                />
                <input
                  tabIndex={-1}
                  autoComplete="off"
                  className={styles.selectHiddenInput}
                  value={selectedGender}
                  required
                />
              </label>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="position" className={styles.labelSmaller}>
                <p className={styles.text}>
                  Ημ. Γέννησης
                  <span className={styles.span}>*</span>
                </p>
                <DatePicker
                  className={styles.input}
                  popperClassName={styles.calendar}
                  selected={startDate}
                  onChange={onChangeDate}
                  dateFormat="dd/MM/yyyy"
                  showYearDropdown
                  dateFormatCalendar="MMMM"
                  yearDropdownItemNumber={120}
                  scrollableYearDropdown
                  required
                />
              </label>
            </div>
            <div className={styles.container}>
              <label htmlFor="address" className={styles.labelFullWidth}>
                <p className={styles.text}>Διεύθυνση Κατοικίας</p>
                <input
                  type="text"
                  id="address"
                  name="address"
                  className={styles.input}
                  value={values.number}
                  onChange={onChangeInput}
                />
              </label>
            </div>
            <div className={styles.container}>
              <label htmlFor="addressNumber" className={styles.labelSmaller}>
                <p className={styles.text}>Αριθμός Κατοικίας</p>
                <input
                  type="text"
                  id="addressNumber"
                  name="addressNumber"
                  className={styles.input}
                  value={values.number}
                  onChange={onChangeInput}
                />
              </label>
              <label htmlFor="postal" className={styles.labelSmaller}>
                <p className={styles.text}>
                  Ταχυδρομικός κώδικας
                  <span className={styles.span}>*</span>
                </p>
                <input
                  type="number"
                  id="postal"
                  name="postal"
                  required
                  className={styles.input}
                  value={values.number}
                  onChange={onChangeInput}
                />
              </label>
            </div>
            <div className={styles.container}>
              <label htmlFor="city" className={styles.labelSmaller}>
                <p className={styles.text}>
                  Πόλη/Περιοχή Κατοικίας
                  <span className={styles.span}>*</span>
                </p>
                <input
                  type="text"
                  id="city"
                  name="city"
                  required
                  className={styles.input}
                  value={values.number}
                  onChange={onChangeInput}
                />
              </label>
              <label htmlFor="telephone" className={styles.labelSmaller}>
                <p className={styles.text}>Τηλεφωνικός Αριθμός</p>
                <input
                  type="number"
                  id="telephone"
                  name="telephone"
                  className={styles.input}
                  value={values.number}
                  onChange={onChangeInput}
                />
              </label>
            </div>
          </>
        )}
        <div className={styles.container}>
          <label htmlFor="mobilePhone" className={styles.labelSmaller}>
            <p className={styles.text}>
              Κινητό Τηλέφωνο<span className={styles.span}>*</span>
            </p>

            <input
              type="number"
              id="mobilePhone"
              name="mobilePhone"
              required
              className={styles.input}
              value={values.number}
              onChange={onChangeInput}
            />
          </label>
          <label htmlFor="email" className={styles.labelSmaller}>
            <p className={styles.text}>
              Διεύθυνση Email
              <span className={styles.span}>*</span>
            </p>
            <input
              type="email"
              id="email"
              name="email"
              required
              className={styles.input}
              value={values.email}
              onChange={onChangeInput}
            />
          </label>
        </div>
        {slug === 'career' && (
          <>
            <div className={styles.container}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="position" className={styles.labelSmaller}>
                <p className={styles.text}>
                  Θέση
                  <span className={styles.span}>*</span>
                </p>
                <Select
                  name="position"
                  inputId="position"
                  options={availablePositions}
                  styles={style}
                  className={styles.selectInput}
                  value={selectedPosition}
                  onChange={onChangeSelectedPosition}
                  placeholder="Επίλεξε μια από τις διαθέσιμες θέσεις"
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  theme={setTheme}
                />
                <input
                  tabIndex={-1}
                  autoComplete="off"
                  className={styles.selectHiddenInput}
                  value={selectedPosition}
                  required
                />
              </label>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="area" className={styles.labelSmaller}>
                <p className={styles.text}>
                  Περιοχή
                  <span className={styles.span}>*</span>
                </p>
                <Select
                  name="area"
                  inputId="area"
                  options={availableAreas}
                  styles={style}
                  className={styles.selectInput}
                  value={selectedArea}
                  onChange={onChangeSelectedArea}
                  placeholder="Επίλεξε μια από τις διαθέσιμες περιοχές"
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  theme={setTheme}
                />
                <input
                  tabIndex={-1}
                  autoComplete="off"
                  className={styles.selectHiddenInput}
                  value={selectedArea}
                  required
                />
              </label>
            </div>

            <div className={styles.fileUpload}>
              <label htmlFor="cv" className={styles.label}>
                <p className={styles.text}>Eπισύναψη βιογραφικού σημειώματος</p>
                <input
                  type="file"
                  name="cv"
                  id="cv"
                  accept="application/pdf,application/msword,
                  application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className={styles.inputUpload}
                  onChange={onUploadFile}
                />
                <div className={styles.inputFake}>{inputText}</div>
              </label>
            </div>
          </>
        )}
        <label htmlFor="message" className={styles.label}>
          <p className={styles.text}>
            Το Μήνυμά σας
            <span className={styles.span}>*</span>
          </p>

          <textarea
            name="message"
            id="message"
            cols="30"
            rows="5"
            required
            value={values.message}
            className={styles.input}
            onChange={onChangeInput}
          />
        </label>
        <div className={styles.buttonContainer}>
          <PrimaryButton type="submit">Αποστολή Μηνύματος</PrimaryButton>
        </div>
      </form>

      {modal.isOpen && (
        <GenericModalV2
          mainText={modal.mainText}
          modalLabel="Info modal"
          onClickContinue={modal.onClickContinue}
          submitText={modal.submitText}
        />
      )}
    </>
  );
}
